/**
 * Shared layout for the public auth routes (`/login`, `/signup`).
 *
 * The router is code-based (see `src/router.tsx`) so this file does not
 * register a TanStack Router layout route directly. Instead it exports
 * `<AuthLayout>` — both the login and signup pages compose it as their
 * root element so the split-screen treatment stays in one place.
 *
 * Layout:
 *  - lg+: 60/40 split — animated gradient backdrop on the left, form card
 *    on the right.
 *  - sm/md: form card stacks on top; backdrop hides on small screens to
 *    keep the form above the fold.
 */
import { type ReactNode } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, FileCheck2, Search, Wallet } from 'lucide-react';
import { fadeUp, stagger } from '@/components/motion';
import { cn } from '@/lib/cn';

interface AuthLayoutProps {
  children: ReactNode;
  /** Optional className override for the right-hand panel inner wrapper. */
  className?: string;
}

const VALUE_PROPS = [
  {
    icon: FileCheck2,
    title: 'Automated audits',
    description: 'Crawl, score and surface issues in minutes — not days.',
  },
  {
    icon: Sparkles,
    title: 'AI-written briefs',
    description: 'Outline, meta variants and internal links, on demand.',
  },
  {
    icon: Search,
    title: 'Keyword research at $0 marginal cost',
    description: 'Seed-to-cluster expansion that runs on free tier APIs.',
  },
] as const;

export function AuthLayout({ children, className }: AuthLayoutProps): JSX.Element {
  return (
    <div className="relative flex min-h-screen overflow-hidden bg-background">
      {/* Left panel — gradient backdrop + value props (lg+ only) */}
      <aside
        aria-hidden="false"
        className="relative hidden flex-1 overflow-hidden lg:flex lg:basis-3/5"
      >
        {/* Layered gradient background */}
        <div className="absolute inset-0 bg-gradient-primary opacity-90" />
        <div
          className="absolute inset-0 mix-blend-overlay opacity-40"
          style={{
            backgroundImage:
              'radial-gradient(700px 400px at 20% 30%, hsl(0 0% 100% / 0.18), transparent 60%),' +
              'radial-gradient(600px 380px at 80% 80%, hsl(280 91% 80% / 0.35), transparent 60%)',
          }}
        />
        {/* Drifting orbs */}
        <DriftingOrbs />
        {/* Subtle grid */}
        <div
          aria-hidden
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage:
              'linear-gradient(to right, hsl(0 0% 100% / 0.18) 1px, transparent 1px),' +
              'linear-gradient(to bottom, hsl(0 0% 100% / 0.18) 1px, transparent 1px)',
            backgroundSize: '48px 48px',
            maskImage: 'radial-gradient(ellipse at center, black 30%, transparent 80%)',
          }}
        />

        {/* Foreground content */}
        <motion.div
          variants={stagger}
          initial="hidden"
          animate="show"
          className="relative z-10 flex h-full w-full flex-col justify-between p-12 text-primary-foreground"
        >
          <motion.div variants={fadeUp} className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/15 backdrop-blur-md ring-1 ring-white/30">
              <Sparkles className="h-5 w-5" />
            </div>
            <span className="text-lg font-semibold tracking-tight">SEO AI Agent</span>
          </motion.div>

          <div className="max-w-xl space-y-10">
            <motion.div variants={fadeUp} className="space-y-3">
              <h2 className="text-5xl font-semibold leading-[1.05] tracking-tight">
                SEO that ships itself.
              </h2>
              <p className="text-lg text-white/80">
                Audit, research and brief — automated end-to-end. Built for teams that
                refuse to babysit dashboards.
              </p>
            </motion.div>

            <motion.ul variants={stagger} className="space-y-5">
              {VALUE_PROPS.map((vp) => {
                const Icon = vp.icon;
                return (
                  <motion.li
                    key={vp.title}
                    variants={fadeUp}
                    className="flex items-start gap-4"
                  >
                    <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/15 backdrop-blur-md ring-1 ring-white/25">
                      <Icon className="h-4 w-4" />
                    </div>
                    <div>
                      <div className="font-medium">{vp.title}</div>
                      <div className="text-sm text-white/75">{vp.description}</div>
                    </div>
                  </motion.li>
                );
              })}
            </motion.ul>
          </div>

          <motion.div variants={fadeUp} className="flex items-center gap-2 text-sm text-white/70">
            <Wallet className="h-4 w-4" />
            <span>Free tier. No credit card. Bring your own LLM key.</span>
          </motion.div>
        </motion.div>
      </aside>

      {/* Right panel — form */}
      <main className="relative flex flex-1 items-center justify-center px-4 py-12 sm:px-8 lg:basis-2/5">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
          className={cn('w-full max-w-md', className)}
        >
          {/* Mobile-only header (since left panel is hidden). Plain text —
              the viewer is logged out, so a link to "/" would just bounce
              straight back here via the auth guard. */}
          <div className="mb-8 flex items-center gap-3 lg:hidden">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-primary text-primary-foreground">
              <Sparkles className="h-4 w-4" />
            </div>
            <span className="text-base font-semibold tracking-tight text-foreground">
              SEO AI Agent
            </span>
          </div>
          {children}
        </motion.div>
      </main>
    </div>
  );
}

/**
 * Three blurred, slowly-drifting orbs that give the left panel a subtle
 * sense of motion. Positioned absolutely so they don't affect layout.
 */
function DriftingOrbs(): JSX.Element {
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0">
      <motion.div
        className="absolute -left-20 top-10 h-72 w-72 rounded-full bg-white/30 blur-3xl"
        animate={{ x: [0, 40, -20, 0], y: [0, 30, -10, 0] }}
        transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div
        className="absolute right-0 top-1/3 h-80 w-80 rounded-full bg-fuchsia-400/40 blur-3xl"
        animate={{ x: [0, -30, 25, 0], y: [0, 20, -25, 0] }}
        transition={{ duration: 22, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div
        className="absolute bottom-0 left-1/3 h-72 w-72 rounded-full bg-sky-300/40 blur-3xl"
        animate={{ x: [0, 25, -15, 0], y: [0, -25, 15, 0] }}
        transition={{ duration: 20, repeat: Infinity, ease: 'easeInOut' }}
      />
    </div>
  );
}
